/**
 * Huddle platform services for the web/desktop shell: the HuddleApi, the (lazy) call engine and the stream registry
 * LiveVideo reads from. One set per app container. Desktop on Linux can't do calls (WebKitGTK has no WebRTC), so
 * there the shell offers "Open huddle in browser" (apps/desktop/INTEGRATION.md §5).
 */
import type { CallEngine, HuddleApi } from "@bundle/core";
import type { Container } from "../container.ts";
import { callsInBrowser } from "../desktop.ts";
import { API_BASE, WEB_ORIGIN } from "../env.ts";
import { withHuddleAnalytics } from "../telemetryApi.ts";
import { createHuddleApi, createMockHuddleApi, freshToken } from "./api.ts";
import { createCallEngine } from "./engine.ts";
import { StreamRegistry } from "./sources.ts";

export type HuddlePlatform = {
  api: HuddleApi;
  engine: CallEngine;
  streams: StreamRegistry;
  /** This client can't carry calls (Linux desktop). */
  browserOnly: boolean;
  /** Opens a path of the web app in the system browser. */
  openInBrowser: (path: string) => void;
};

const byContainer = new WeakMap<Container, HuddlePlatform>();

export function huddlePlatform(container: Container): HuddlePlatform {
  const existing = byContainer.get(container);
  if (existing !== undefined) return existing;
  const streams = new StreamRegistry();
  const p: HuddlePlatform = {
    api: container.mock
      ? createMockHuddleApi(() => container.session.state.session?.userId ?? "")
      : withHuddleAnalytics(createHuddleApi(container.session), container.analytics, () => container.session.state.session?.userId),
    engine: createCallEngine({ token: () => freshToken(container.session), sources: streams, apiBase: API_BASE }),
    streams,
    browserOnly: callsInBrowser(),
    // Desktop: window.open of an external URL goes to the system browser (the opener plugin intercepts it).
    openInBrowser: (path) => window.open(`${WEB_ORIGIN}${path}`, "_blank", "noopener"),
  };
  byContainer.set(container, p);
  return p;
}
